import * as Sentry from '@sentry/react';
import { isElectron } from '@utils/analytics/platform';
import { isNonInteractiveAgent } from '@utils/analytics/nonInteractiveAgent';
import { noteExpectedFailure } from '@utils/observability/expectedFailure';

// Why the panel does not always register the PWA service worker.
//
// Inside the Electron desktop shell web push is dead: Sentry PLICK-FRONT-PANEL-COMERCIO-Z7
// collected 1,529 events of "Registration failed - push service not available" and 1,529 of 1,529
// carried `browser.name:Electron` — the Chromium build Electron ships has no push service, so
// `PushManager.subscribe` can never succeed there. The desktop hears about orders over the
// websocket (`useIncomingOrder`) and announces them with the plain Notification API
// (`utils/notifications/orderDesktopNotify`), never through FCM. What the registration did buy was
// noise: PLICK-FRONT-PANEL-COMERCIO-ZE, 1,193 events / 294 users, 1,192 of them `Electron 24.8.8`
// (`InvalidStateError`, DOMException code 11 — consistent with the document's execution context
// being gone before workbox's deferred `register()` lands).
//
// Precaching is a net negative there too: the shell reloads the panel on every launch with
// `pragma: no-cache` and ships its own Squirrel auto-updater, so the worker can only serve a build
// that is older than what the shell just fetched.
export const shouldRegisterServiceWorker = (): boolean => !isElectron();

// Desktops that registered the worker BEFORE this gate keep it forever: skipping `register()` never
// removes an existing registration, so those panels would stay pinned to a precached build. Best
// effort, and it lands on the NEXT launch — the live document keeps its current controller.
export const unregisterStaleServiceWorker = async (): Promise<void> => {
  try {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Cleanup must never break the boot.
  }
};

// Everything that reaches this reporter from a real browser is "supports service workers and failed
// anyway": a commerce that will not get push while the panel is closed. That is the signal to keep,
// so the only thing filtered out here is the traffic that has nobody behind it.
export const capturePwaRegistrationError = (error: Error): void => {
  try {
    if (isNonInteractiveAgent()) {
      noteExpectedFailure('pwa', 'service worker registration failed on a non-interactive agent', {
        errorName: error.name,
        errorMessage: error.message,
      });
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('feature', 'pwa');
      scope.setTag('action', 'sw_registration_error');
      // Group by failure class, not by the minified workbox stack: AbortError (script fetch),
      // SecurityError (blocked script) and NotSupportedError are different incidents that used to
      // land in the same issue because the frames are identical.
      scope.setFingerprint(['pwa', 'sw_registration_error', error.name || 'Error']);
      scope.setContext('pwa', { errorName: error.name, errorMessage: error.message });
      Sentry.captureException(error);
    });
  } catch {
    // Observability must never break the boot.
  }
};
